import React, { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkle, Trophy, Warning, ChartLine, TrendUp, TrendDown, Gauge, ClockClockwise, FilePdf } from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { buildGapAnalysisPdf } from '@/lib/wealthPdf';
import HelpHint from '@/components/HelpHint';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import CurrencyInput from '@/components/CurrencyInput';

const fmtMoney = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtPct = (n) => `${Math.round(Number(n || 0) * 1000) / 10}%`;

const PRESETS = {
  conservative: { label: 'Conservative', inflation: 0.04, pre_return: 0.05, post_return: 0.035, ss_cola: 0.02 },
  moderate:     { label: 'Moderate',     inflation: 0.03, pre_return: 0.07, post_return: 0.05,  ss_cola: 0.025 },
  optimistic:   { label: 'Optimistic',   inflation: 0.025, pre_return: 0.085, post_return: 0.06, ss_cola: 0.03 },
};

const IMPACT_COLORS = {
  Low:      { bg: '#2D6A4F15', fg: '#2D6A4F' },
  Moderate: { bg: '#B7791F15', fg: '#B7791F' },
  High:     { bg: '#C5303015', fg: '#C53030' },
};

const defaultInputs = () => ({
  current_age: 50,
  retirement_age: 65,
  life_expectancy: 90,
  current_annual_spending: 72000,
  preset: 'moderate',
  inflation: 0.03,
  pre_return: 0.07,
  post_return: 0.05,
  ss_cola: 0.025,
  traditional_balance: 320000,
  roth_balance: 45000,
  brokerage_balance: 80000,
  monthly_contribution_traditional: 1500,
  monthly_contribution_roth: 250,
  monthly_contribution_brokerage: 0,
  social_security_monthly: 2400,
  social_security_start_age: 67,
  pension_monthly: 0,
  pension_start_age: 65,
  rental_monthly: 0,
  business_monthly: 0,
  dividend_monthly: 100,
  other_monthly: 0,
});

const Field = ({ label, name, value, onChange, type = 'number', step, testid, suffix, currency = false }) => (
  <div>
    <Label className="text-xs text-[#4A5568]">{label}</Label>
    <div className="relative">
      {currency ? (
        <CurrencyInput
          value={value}
          onValue={(v) => onChange(name, v === null ? 0 : v)}
          testid={testid}
        />
      ) : (
        <Input
          type={type}
          step={step}
          value={value}
          onChange={(e) => onChange(name, type === 'number' ? Number(e.target.value) : e.target.value)}
          data-testid={testid}
          className="border-slate-300"
        />
      )}
      {suffix && !currency && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A0AABF]">{suffix}</span>}
    </div>
  </div>
);

const KpiCard = ({ label, value, sub, color = '#0A1128', tone = 'light', testid }) => (
  <div
    className={`rounded-md p-5 dashboard-card border ${
      tone === 'dark' ? 'bg-[#0A1128] border-[#0A1128]' : 'bg-white border-slate-200'
    }`}
    data-testid={testid}
  >
    <p className={`text-xs font-semibold uppercase tracking-[0.1em] mb-2 ${tone === 'dark' ? 'text-[#C5A059]' : 'text-[#4A5568]'}`}>{label}</p>
    <p className={`text-2xl font-semibold ibm-plex ${tone === 'dark' ? 'text-white' : ''}`} style={tone === 'dark' ? {} : { color }}>{value}</p>
    {sub && <p className={`text-xs mt-1 ${tone === 'dark' ? 'text-[#A0AABF]' : 'text-[#A0AABF]'}`}>{sub}</p>}
  </div>
);

const RetirementGapAnalyzer = () => {
  useTrackToolUsage('retirement_gap_analyzer');
  const [inputs, setInputs] = useState(defaultInputs);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handle = (name, value) => setInputs((p) => ({ ...p, [name]: value }));

  const applyPreset = (key) => {
    const p = PRESETS[key];
    setInputs((prev) => ({
      ...prev,
      preset: key,
      inflation: p.inflation,
      pre_return: p.pre_return,
      post_return: p.post_return,
      ss_cola: p.ss_cola,
    }));
    toast.success(`Applied ${p.label} assumptions`);
  };

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const res = await api.post('/gap/analyze', inputs);
      setResult(res.data);
      toast.success('Gap analysis complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!result) return;
    try {
      let wealthHealth = null;
      try {
        const whsRes = await api.get('/wealth-health-score');
        wealthHealth = whsRes.data;
      } catch (e) {
        // WHS optional — continue without it
      }
      const doc = buildGapAnalysisPdf({ inputs, result, wealthHealth });
      doc.save(`planivance-retirement-gap-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  const chartData = result?.baseline?.yearly?.map((y) => ({
    age: y.age,
    spending: y.spending,
    income: y.income,
    balance: y.balance,
  })) || [];

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Retirement Income Gap Analyzer™:</strong> Educational scenario modeling. Compares your projected retirement spending against expected income streams and presents six gap-closure strategies for side-by-side study. Not personalized investment, tax, or legal advice.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Retirement Income Gap Analyzer<sup className="text-base">™</sup>
          </h1>
          <HelpHint
            topic="How the Retirement Income Gap Analyzer™ works"
            query="retirement gap analyzer closure scenarios"
            testid="gap-help-hint"
          />
        </div>
        <p className="text-base text-[#4A5568]">
          Quantify your retirement shortfall and explore six paths to close it.
        </p>
      </div>

      {/* Assumption Presets */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="cabinet-grotesk text-lg font-medium text-[#0A1128] flex items-center gap-2">
            <Gauge size={20} weight="duotone" className="text-[#C5A059]" />
            Planning Assumptions
          </h2>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            data-testid="toggle-advanced-assumptions"
            className="text-xs text-[#0A1128] underline"
          >
            {showAdvanced ? 'Hide custom' : 'Customize'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {Object.entries(PRESETS).map(([key, p]) => {
            const active = inputs.preset === key;
            return (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                data-testid={`preset-${key}`}
                className={`text-left p-4 rounded-md border transition-all ${
                  active
                    ? 'bg-[#0A1128] text-white border-[#0A1128]'
                    : 'bg-white border-slate-200 hover:border-[#C5A059]'
                }`}
              >
                <p className={`text-xs font-semibold uppercase tracking-[0.1em] ${active ? 'text-[#C5A059]' : 'text-[#4A5568]'}`}>{p.label}</p>
                <p className={`text-xs mt-2 ${active ? 'text-[#A0AABF]' : 'text-[#4A5568]'}`}>
                  Inflation {fmtPct(p.inflation)} · Pre {fmtPct(p.pre_return)} · Post {fmtPct(p.post_return)} · SS COLA {fmtPct(p.ss_cola)}
                </p>
              </button>
            );
          })}
        </div>
        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-200">
            <Field label="Inflation" name="inflation" value={inputs.inflation} step="0.001" onChange={handle} testid="input-inflation" />
            <Field label="Pre-Ret Return" name="pre_return" value={inputs.pre_return} step="0.001" onChange={handle} testid="input-pre-return" />
            <Field label="Post-Ret Return" name="post_return" value={inputs.post_return} step="0.001" onChange={handle} testid="input-post-return" />
            <Field label="SS COLA" name="ss_cola" value={inputs.ss_cola} step="0.001" onChange={handle} testid="input-ss-cola" />
          </div>
        )}
      </div>

      {/* Demographics + Spending */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-lg font-medium text-[#0A1128] mb-4">Your Profile</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Current Age" name="current_age" value={inputs.current_age} onChange={handle} testid="input-current-age" />
          <Field label="Retirement Age" name="retirement_age" value={inputs.retirement_age} onChange={handle} testid="input-retirement-age" />
          <Field label="Life Expectancy" name="life_expectancy" value={inputs.life_expectancy} onChange={handle} testid="input-life-expectancy" />
          <Field label="Annual Spending Today ($)" name="current_annual_spending" value={inputs.current_annual_spending} onChange={handle} testid="input-current-spending" currency />
        </div>
      </div>

      {/* Assets */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-lg font-medium text-[#0A1128] mb-4">Asset Balances & Contributions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Traditional Balance ($)" name="traditional_balance" value={inputs.traditional_balance} onChange={handle} testid="input-trad-balance" currency />
          <Field label="Roth Balance ($)" name="roth_balance" value={inputs.roth_balance} onChange={handle} testid="input-roth-balance" currency />
          <Field label="Brokerage Balance ($)" name="brokerage_balance" value={inputs.brokerage_balance} onChange={handle} testid="input-brokerage-balance" currency />
          <Field label="Monthly → Traditional ($)" name="monthly_contribution_traditional" value={inputs.monthly_contribution_traditional} onChange={handle} testid="input-mc-trad" currency />
          <Field label="Monthly → Roth ($)" name="monthly_contribution_roth" value={inputs.monthly_contribution_roth} onChange={handle} testid="input-mc-roth" currency />
          <Field label="Monthly → Brokerage ($)" name="monthly_contribution_brokerage" value={inputs.monthly_contribution_brokerage} onChange={handle} testid="input-mc-brokerage" currency />
        </div>
      </div>

      {/* Income Streams */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-lg font-medium text-[#0A1128] mb-4">Expected Retirement Income (today's dollars, monthly)</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Social Security ($/mo)" name="social_security_monthly" value={inputs.social_security_monthly} onChange={handle} testid="input-ss-monthly" currency />
          <Field label="SS Start Age" name="social_security_start_age" value={inputs.social_security_start_age} onChange={handle} testid="input-ss-age" />
          <Field label="Pension ($/mo)" name="pension_monthly" value={inputs.pension_monthly} onChange={handle} testid="input-pension" currency />
          <Field label="Rental Income ($/mo)" name="rental_monthly" value={inputs.rental_monthly} onChange={handle} testid="input-rental" currency />
          <Field label="Business Income ($/mo)" name="business_monthly" value={inputs.business_monthly} onChange={handle} testid="input-business" currency />
          <Field label="Dividend Income ($/mo)" name="dividend_monthly" value={inputs.dividend_monthly} onChange={handle} testid="input-dividend" currency />
          <Field label="Other Income ($/mo)" name="other_monthly" value={inputs.other_monthly} onChange={handle} testid="input-other" currency />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={handleAnalyze}
          disabled={loading}
          data-testid="analyze-gap-button"
          className="bg-[#0A1128] hover:bg-[#152145]"
        >
          <ChartLine size={18} className="mr-2" />
          {loading ? 'Calculating...' : 'Analyze Gap'}
        </Button>
        {result && (
          <Button
            onClick={handleExportPdf}
            variant="outline"
            data-testid="export-gap-pdf-button"
            className="border-[#C5A059] text-[#0A1128]"
          >
            <FilePdf size={18} className="mr-2" />
            Save Plan as PDF
          </Button>
        )}
        {result && (
          <p className="text-xs text-[#A0AABF]">
            Using {result.preset_used.charAt(0).toUpperCase() + result.preset_used.slice(1)} assumptions ·
            Inflation {fmtPct(result.assumptions.inflation)} · Pre {fmtPct(result.assumptions.pre_return)} · Post {fmtPct(result.assumptions.post_return)} · COLA {fmtPct(result.assumptions.ss_cola)}
          </p>
        )}
      </div>

      {result && (
        <>
          {/* Baseline KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gap-baseline-kpis">
            <KpiCard
              label="Annual Spending Need"
              value={fmtMoney(result.baseline.annual_spending_at_retirement)}
              sub={`at age ${inputs.retirement_age}, inflation-adjusted`}
              testid="kpi-spending"
            />
            <KpiCard
              label="Expected Annual Income"
              value={fmtMoney(result.baseline.annual_income_at_retirement)}
              sub="all retirement streams combined"
              testid="kpi-income"
            />
            <KpiCard
              label={result.baseline.annual_gap > 0 ? 'Annual Shortfall' : 'Annual Surplus'}
              value={fmtMoney(Math.abs(result.baseline.annual_gap))}
              sub={result.baseline.annual_gap > 0 ? 'must come from assets' : 'income exceeds spending'}
              color={result.baseline.annual_gap > 0 ? '#C53030' : '#2D6A4F'}
              testid="kpi-gap"
            />
            <KpiCard
              label="Retirement Readiness"
              value={`${result.baseline.readiness_score}%`}
              sub={
                result.baseline.depletion_age
                  ? `assets last ~${result.baseline.years_lasting} yrs, deplete at ${result.baseline.depletion_age}`
                  : `assets sustain through age ${inputs.life_expectancy}`
              }
              tone="dark"
              testid="kpi-readiness"
            />
          </div>

          {/* Cashflow Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="gap-cashflow-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Retirement Cash Flow Projection</h2>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="age" stroke="#4A5568" />
                <YAxis stroke="#4A5568" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
                <Area yAxisId={0} type="monotone" dataKey="balance" name="Portfolio Balance" stroke="#C5A059" fill="url(#balGrad)" strokeWidth={2} />
                <Line yAxisId={0} type="monotone" dataKey="spending" name="Annual Spending" stroke="#C53030" strokeWidth={2} dot={false} />
                <Line yAxisId={0} type="monotone" dataKey="income" name="Annual Income" stroke="#0A1128" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-xs text-[#A0AABF] mt-3">
              Lifetime tax estimate (rough): <strong className="text-[#1A202C]">{fmtMoney(result.baseline.lifetime_tax_estimate)}</strong> at a simplified 22% effective rate on ordinary-income streams + traditional withdrawals.
            </p>
          </div>

          {/* Scenarios */}
          <div>
            <h2 className="cabinet-grotesk text-2xl font-medium text-[#0A1128] mb-4">Gap Closure Scenarios</h2>
            <p className="text-sm text-[#4A5568] mb-6">
              Each scenario re-runs the simulation with a single change. The "Strongest Effect" badge marks the scenario that produces the largest readiness improvement. <strong>No single path is recommended</strong> — review tradeoffs and consult a qualified planner.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gap-scenarios-grid">
              {result.scenarios.map((s) => {
                const impact = IMPACT_COLORS[s.lifestyle_impact] || IMPACT_COLORS.Moderate;
                const better = s.readiness_delta > 0;
                return (
                  <div
                    key={s.id}
                    className={`rounded-md p-5 border-2 transition-all ${
                      s.strongest_effect ? 'bg-[#0A1128] text-white border-[#C5A059]' : 'bg-white border-slate-200 hover:border-[#C5A059]'
                    }`}
                    data-testid={`scenario-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className={`cabinet-grotesk text-base font-medium ${s.strongest_effect ? 'text-[#C5A059]' : 'text-[#0A1128]'}`}>
                        {s.label}
                      </h3>
                      {s.strongest_effect && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C5A059] text-[#0A1128] text-[10px] font-semibold uppercase tracking-[0.08em] rounded">
                          <Trophy size={10} weight="duotone" />
                          Strongest Effect
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mb-4 leading-relaxed ${s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}`}>
                      {s.description}
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className={s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}>Readiness</span>
                        <span className={`font-semibold ibm-plex ${s.strongest_effect ? 'text-white' : 'text-[#0A1128]'}`}>
                          {s.readiness_score}%
                          <span className={`ml-2 text-xs font-medium ${better ? 'text-[#2D6A4F]' : s.readiness_delta < 0 ? 'text-[#C53030]' : 'text-[#A0AABF]'}`}>
                            ({s.readiness_delta > 0 ? '+' : ''}{s.readiness_delta})
                          </span>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}>Gap Reduction</span>
                        <span className={`font-semibold ibm-plex ${s.strongest_effect ? 'text-white' : 'text-[#0A1128]'}`}>
                          {fmtMoney(s.gap_reduction)}/yr
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}>Asset Longevity</span>
                        <span className={`font-semibold ibm-plex ${s.strongest_effect ? 'text-white' : 'text-[#0A1128]'}`}>
                          {s.years_lasting} yrs
                          <span className={`ml-2 text-xs font-medium ${s.years_lasting_delta > 0 ? 'text-[#2D6A4F]' : s.years_lasting_delta < 0 ? 'text-[#C53030]' : 'text-[#A0AABF]'}`}>
                            ({s.years_lasting_delta > 0 ? '+' : ''}{s.years_lasting_delta})
                          </span>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}>Lifetime Tax Δ</span>
                        <span className={`font-semibold ibm-plex ${
                          s.lifetime_tax_delta < 0 ? 'text-[#2D6A4F]' : s.lifetime_tax_delta > 0 ? (s.strongest_effect ? 'text-[#C5A059]' : '#C53030') : ''
                        }`} style={s.lifetime_tax_delta > 0 && !s.strongest_effect ? { color: '#C53030' } : {}}>
                          {s.lifetime_tax_delta > 0 ? '+' : ''}{fmtMoney(s.lifetime_tax_delta)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: s.strongest_effect ? '#1F2A4A' : '#E2E8F0' }}>
                        <span className={s.strongest_effect ? 'text-[#A0AABF]' : 'text-[#4A5568]'}>Lifestyle Impact</span>
                        <span
                          className="px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-[0.06em]"
                          style={{ color: impact.fg, backgroundColor: impact.bg }}
                        >
                          {s.lifestyle_impact}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-[#4A5568] leading-relaxed">
            <strong>Educational Use Only:</strong> All projections are deterministic, assume constant returns / inflation, and use a simplified 22% effective tax rate for the lifetime tax estimate. Actual market returns, tax rates, healthcare costs, and longevity will vary materially. This tool is not investment, tax, or legal advice. Consult qualified professionals before making decisions.
          </p>
        </>
      )}
    </div>
  );
};

export default RetirementGapAnalyzer;
